import React, { Component, createRef } from 'react';
import { Button, Col, Progress } from 'reactstrap';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import cuid from 'cuid';
import { get } from 'lodash-es';
import isEqual from 'react-fast-compare';
import { RotateLeftRounded, RotateRightOutlined } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import parseFileName from './parseFileName';
import getLotExtension from './getLotExtension';
import { imagePath, isProd } from '../../../utilities/UrlPaths';
import ImageRotateViewer from '../ImageRotateViewer';

// 10 MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const NO_IMAGE = `${imagePath}/images/NoImage.png`;

// Default Axios requests to retry 3 additional times if they fail
axiosRetry(axios, {
  retries: 3,
  retryDelay: () => 3000
});

/**
 * Each file has its own progress state.  As soon as this file is created - we
 * mark it as uploading and it enters the queue.  Upon successful processing we mark it as
 * complete.
 *
 * If the file has an error - we show text underneath and give a red border.
 */
class Step4File extends Component {
  constructor(props) {
    super(props);
    this.imageRef = createRef();
    // Cancel Token provides a means for a user to cancel the CPS request for image uploading
    this.cancelToken = null;
    /**
     * xhr represents the XMLHTTPRequest - this needs to have larger scope so
     * that we can 'abort' it if a user clicks cancel upload
     */
    this.xhr = null;
    this.remainingAwsRetries = 5;

    this.state = {
      filterOnlyErrors: props.filterOnlyErrors,
      hasError: false,
      imageS3URL: null,
      imageThumbnail: null,
      index: props.index,
      // isSelected: false,
      isUploading: false,
      progress: 0,
      showEditImageModal: false,
      newFileUploaded: false,
      rotate: 0
    };
  }

  async uploadNewFile() {
    const { addFileError, file, fileUploadFormat, index } = this.props;
    // Step 1 - If the file already has an error - meaning it was rejected by dropzone - mark done!
    if (file.hasError) {
      return;
    }

    // Step 2 - Test file size
    if (file.size > MAX_FILE_SIZE) {
      addFileError('size', index);
      this.setState({ isUploading: false });
      return;
    }

    /**
     * Step 3 - Test the file's image dimensions (this loads the thumbnail)
     * The try/catch resolves the uncaught promise error - and potentially
     * a few safari specific bugs with loading images since the default
     * try/catch handler won’t catch promises thrown by testImageDimensions
     */
    //  COMMENT OUT this test for now`
    /*
    try {
      await this.testImageDimensions(file);
    } catch (e) {
      addFileError(e.message, index);
      this.setState({ isUploading: false });
      return;
    }
    */

    //  this instead that allows small images and shows a warning
    try {
      let smallImageCheck = false;
      const imageTest = new Image();
      imageTest.src = URL.createObjectURL(file);
      imageTest.onload = () => {
        if (imageTest.naturalWidth < 1000 || imageTest.naturalHeight < 1000) {
          smallImageCheck = true;
        }
        this.props.handleImageSizeCheck(smallImageCheck);
      };
    } catch (e) {
      console.log(e);
    }

    //  add this here since it's a part of TestImageDimensions, which we're removing
    const imageThumbnail = URL.createObjectURL(file);
    const imageTest = new Image();
    imageTest.src = imageThumbnail;
    // Combine the upload 'start' here to reduce the total number of renders
    this.setState({ isUploading: true, imageThumbnail });

    // Step 4 - Test to make sure we match the lot/inventory number list
    const parsedFileName = parseFileName(file.name);
    const splitFile = parsedFileName.split('.');
    /**
     * This may or may not have the lot extension also so we have to parse
     * it if the upload is by lotNumber and not inventoryNumber
     */
    const lotOrInventoryNumber = splitFile[0];

    const imageSeq = splitFile.length >= 3 ? splitFile[1] : '0';
    const imageSeqIsNumber = /^\d+$/.test(imageSeq);
    if (!imageSeqIsNumber) {
      addFileError('imageSeq', index);
      this.setState({ isUploading: false });
      return;
    }

    let lotNum = '';
    let lotExt = '';
    let invNumber = '';
    if (fileUploadFormat === 'lotNumber') {
      const { lotNumbers } = this.props;
      if (!lotNumbers.has(lotOrInventoryNumber)) {
        addFileError('lotNum', index);
        this.setState({ isUploading: false });
        return;
      }

      const parseLotName = getLotExtension(lotOrInventoryNumber);
      lotNum = parseLotName.lotNumber;
      lotExt = parseLotName.lotExt;
    } else if (fileUploadFormat === 'inventoryNumber') {
      const { inventoryNumbers } = this.props;

      // We have to find the lot number and lot extension based on the inventory number
      const lotIndex = inventoryNumbers.findIndex(
        inventoryNumber => inventoryNumber.invNumber === lotOrInventoryNumber
      );

      if (lotIndex < 0) {
        addFileError('invNum', index);
        this.setState({ isUploading: false });
        return;
      }

      // We have to find the lot number based on the inventory number
      lotNum = inventoryNumbers[lotIndex].lotNumber;
      lotExt = inventoryNumbers[lotIndex].lotExt;
      invNumber = inventoryNumbers[lotIndex].invNumber;
    }

    this.uploadFile(imageSeq, lotExt, lotNum, invNumber);
  }

  async componentDidMount() {
    await this.uploadNewFile();
  }

  uploadFile = async (imageSeq, lotExt, lotNum, invNumber) => {
    try {
      const { catalogID, handleFileComplete, houseID, fileUploadFormat } = this.props;

      /**
       * (dyouberg) Safari is dumb and caches GET requests, we need to
       * ensure different requests are being made, or else the incorrect
       * image may be uploaded for several thumbnails.
       *
       * https://github.com/ericelliott/cuid
       */
      const uniqueIdentifier = cuid();

      // Step 1 - Get the S3 Bucket URL
      const s3BucketUrl = `https://9ctqzg3xxj.execute-api.us-east-1.amazonaws.com/${
        isProd ? 'prod' : 'stage'
      }/INV-ImageUploader?numImages=0&bucket=/house/${houseID}/catalog/${catalogID}&nocache=${uniqueIdentifier}`;

      const awsUrlResponse = await axios(s3BucketUrl, {
        'axios-retry': {
          retries: 3,
          retryDelay: () => 3000
        }
      });

      const imageURL = get(awsUrlResponse, 'data[0].imageUrl', '');
      const awsPutUrl = get(awsUrlResponse, 'data[0].putUrl', '');

      /**
       * Step 2 - PUT the file to the S3 Bucket URL using the putURL - see function below
       * for the XMLHttpRequest handling
       */
      await this.handlePutRequest(awsPutUrl, imageSeq, lotExt, lotNum, invNumber);

      // Step 3 - PUT to CPS using the imageURL
      const cpsPutUrl = `/cps/app/image/house/${houseID}/catalog/${catalogID}?fromDIY=true&doValidation=false`;
      let cpsPutData = [];

      if (fileUploadFormat === 'lotNumber') {
        cpsPutData = [{ imageSeq, imageURL, lotExt, lotNum }];
      } else {
        cpsPutData = [{ imageSeq, imageURL, lotExt, invNumber }];
      }

      /**
       * Instantiate our cancel token so that we can call it with
       * this.cancelToken.cancel() on an abort
       */
      this.cancelToken = axios.CancelToken.source();

      /**
       * In the event of CPS failing - retry the request with
       * exponential delay added in between attempts
       */
      const cpsPutResponse = await axios.put(cpsPutUrl, cpsPutData, {
        cancelToken: this.cancelToken.token,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const cpsPutResponseError = get(cpsPutResponse, 'data.data.images[0].error', null);
      if (!cpsPutResponseError) {
        const imageS3URL = get(cpsPutResponse, 'data.data.images[0].imageS3URL', null);
        const photoID = get(cpsPutResponse, 'data.data.images[0].photoID', null);
        // Signal file is complete to update overall progress
        const { index } = this.props;
        handleFileComplete(index, photoID);
        this.setState({ imageS3URL, isUploading: false });
      } else {
        const { addFileError, index } = this.props;
        if (cpsPutResponseError === 'Invalid Image') {
          addFileError('invalidImage', index);
        } else {
          addFileError('timeout', index);
        }
        // Mark file as complete to update state for the file component
        this.setState({ isUploading: false });
      }
    } catch (e) {
      /**
       * A promise was rejected and the file was not uploaded - we use addFileError to display
       * the appropriate error message to the user
       */
      const { addFileError, index } = this.props;
      let errorMsg = e.message;
      if (errorMsg === 'abort' || axios.isCancel(e)) {
        // Remove the file from the array - don't need to send an error
        const { abortFile } = this.props;
        abortFile(index);
      } else {
        if (!e.status) {
          errorMsg = 'timeout';
        }
        /**
         * In the event of no network connectivity - axios sends a string and not an object.
         * The !e.status check will be hit for network connectivity issues.
         *
         * See: https://github.com/axios/axios/issues/383
         */
        addFileError(errorMsg, index);
        this.setState({ isUploading: false });
      }
    }
  };

  async componentDidUpdate() {
    if (this.props.file.isUpdatedFile) {
      this.props.isUpdatedFileHandler(this.props.index);
      await this.uploadNewFile();
    }
  }

  /**
   * We need to do this GDSFP and SCU logic in order to re-render if the user
   * uses the select checkbox, otherwise avoid re-renders of these files at all costs!
   */
  static getDerivedStateFromProps(props, state) {
    // (DY Removed) file.isSelected
    const {
      file: { hasError },
      filterOnlyErrors,
      index,
      file
    } = props;

    const stateObj = {};
    if (hasError !== state.hasError) {
      // Needed for the case of a file timeout
      stateObj.hasError = hasError;
    }

    // if (isSelected !== state.isSelected) {
    //   // Trigger a state change if the user clicked the box - so SCU returns true
    //   stateObj.isSelected = isSelected;
    // }

    if (index !== state.index) {
      stateObj.index = index;
    }

    if (filterOnlyErrors !== state.filterOnlyErrors) {
      stateObj.filterOnlyErrors = filterOnlyErrors;
    }

    return stateObj;
  }

  /**
   * Only re-render if the individual file state changes
   * (during upload), or if the user clicks the checkbox
   */
  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.state, nextState) || Boolean(nextProps.file.isUpdatedFile);
  }

  // A file must follow the 1000x1000 minimum pixel requirement
  testImageDimensions = updatedFile =>
    new Promise((resolve, reject) => {
      const { addFileError, index } = this.props;
      const file = updatedFile || this.props.file;
      const imageTest = new Image();

      imageTest.onload = () => {
        if (imageTest.naturalWidth < 1000 || imageTest.naturalHeight < 1000) {
          URL.revokeObjectURL(imageTest.src);
          addFileError('pixel', index);
          reject(new Error('pixel'));
        } else {
          URL.revokeObjectURL(imageTest.src);
          resolve();
        }
      };

      imageTest.onerror = () => {
        URL.revokeObjectURL(imageTest.src);
        const isSafari = navigator.userAgent.toLowerCase().indexOf('safari/') > -1;
        if (isSafari) {
          addFileError('safari', index);
          reject(new Error('safari'));
        } else {
          addFileError('default', index);
          reject(new Error('default'));
        }
        this.setState({ imageThumbnail: null });
      };

      const imageThumbnail = URL.createObjectURL(file);
      imageTest.src = imageThumbnail;
      // Combine the upload 'start' here to reduce the total number of renders
      this.setState({ isUploading: true, imageThumbnail });
    });

  /**
   * Making this a function alleviates some issues with executing the three requests in a
   * synchronous manner.  We need to wait for the PUT request to complete before making the
   * request to CPS.
   *
   * This function solely exists to wrap the XMLHttpRequest functionality into a Promise, and
   * once the Promise resolves - we can then call the CPS request.
   */
  handlePutRequest = (putUrl, imageSeq, lotExt, lotNum, invNumber) =>
    new Promise((resolve, reject) => {
      /**
       * Using the XMLHttpRequest object, we gain more control over the request, which
       * allows us to do things such as control the timeout value, tap into the onprogress
       * event (create a progress bar)
       */
      const { file } = this.props;
      this.xhr = new XMLHttpRequest();

      // Timeout after 5 minutes
      this.xhr.timeout = 1000 * 60 * 5;
      this.xhr.open('PUT', putUrl);

      this.xhr.setRequestHeader('Content-Type', file.type);

      /**
       * WARNING!!! When doing this as a PUT request, this is xhr.upload.onprogress
       * NOT xhr.onprogress!!!
       */
      this.xhr.upload.onprogress = progressEvent => {
        const { loaded, total } = progressEvent;
        const progressPct = Math.round((loaded / total) * 100);
        this.setState({ progress: progressPct });
      };

      this.xhr.onabort = () => reject(new Error('abort'));

      this.xhr.ontimeout = () => {
        if (this.remainingAwsRetries > 0) {
          this.remainingAwsRetries -= 1;
          setTimeout(() => this.uploadFile(imageSeq, lotExt, lotNum, invNumber), 3000);
        } else {
          const { addFileError, index } = this.props;
          addFileError('timeout', index);
          this.setState({ isUploading: false });
        }
      };

      this.xhr.onload = () => resolve();
      this.xhr.send(file);
    });

  // Cancel the XHR / CPS requests if they are still in progress
  cancelUpload = () => {
    if (this.cancelToken) {
      this.cancelToken.cancel();
    }

    if (this.xhr) {
      this.xhr.abort();
    }
  };

  handleRotateChange = isHorizontal => {
    const setRotate = rotate => this.setState({ rotate });
    const rotate = this.state.rotate;
    if (isHorizontal) {
      if (this.state.rotate === 270) setRotate(0);
      else if (rotate >= -270 && rotate < 270) setRotate(rotate + 90);
    } else if (rotate === -270) setRotate(0);
    else if (rotate > -270 && rotate <= 270) setRotate(rotate - 90);
  };

  saveImageHandler() {
    const canvas = this.imageRef.current;
    const file = this.props.file;
    if (canvas) {
      canvas.toBlob(async blob => {
        if (blob) {
          const updatedFile = new File([blob], file.name, { type: file.type });
          this.testImageDimensions(updatedFile);
          this.setState(
            {
              hasError: false,
              imageS3URL: null,
              isUploading: true,
              progress: 0,
              rotate: 0
            },
            () => {
              this.props.updateFileByIndex(updatedFile, this.props.index);
            }
          );
        }
      }, file.type);
    }
  }

  render() {
    // (DY Removed) handleSelectFile
    const { abortFile, file, handleDeleteFile } = this.props;

    // (DY Removed) isSelected
    const {
      filterOnlyErrors,
      hasError,
      imageS3URL,
      imageThumbnail,
      index,
      isUploading,
      progress
    } = this.state;

    if (filterOnlyErrors && !hasError) {
      return null;
    }

    let imageSrc = null;
    const errorCode = get(file, 'error.code', null);
    if (errorCode === 'type' || errorCode === 'timeout') {
      /**
       * For invalid file types - show the no image placeholder
       * In the event of timeouts - theres a potential for a LOT
       * of images to reside in memory - so just show the placeholder
       */
      imageSrc = NO_IMAGE;
    } else if (imageS3URL) {
      /**
       * Once we have image thumbnail on S3 - change the imageSrc to point to S3,
       * and revokeObjectUrl to reclaim image memory
       */
      imageSrc = imageS3URL;
    } else {
      /**
       * When we initially render - this lets us use the thumbnail we
       * loaded when testing image dimensions
       */
      imageSrc = imageThumbnail || NO_IMAGE;
    }

    // Set the appropriate on click handler for the cancel upload / delete button
    let cancelDeleteOnClick = null;
    if (progress === 0) {
      cancelDeleteOnClick = () => abortFile(index);
    } else if (isUploading) {
      cancelDeleteOnClick = () => this.cancelUpload(index);
    } else {
      cancelDeleteOnClick = () => handleDeleteFile(index);
    }

    return (
      <Col xs={3} className="file-container py-3">
        <div className={hasError ? 'border-invalid px-3 pt-2' : 'file-border px-3 pt-2'}>
          <div className="d-flex">
            {/* May re-add in the future... */}
            {/*
            {!isUploading && (
              <Input
                id={`image_${index}`}
                className="ml-0 mr-1 position-relative"
                checked={isSelected}
                onChange={() => handleSelectFile(index)}
                type="checkbox"
              />
            )}
            <Label for={`image_${index}`} className="mr-4 my-0 text-truncate">
            */}
            {file.name}
            {/* }
            </Label>
            */}
          </div>
          <div className="thumb-container d-flex justify-content-center align-items-center mt-2 mb-3">
            {imageSrc && (
              <ImageRotateViewer
                ref={this.imageRef}
                rotate={this.state.rotate}
                className={
                  isUploading
                    ? 'w-100 mh-100'
                    : `w-100 mh-100 ${this.state.rotate ? '' : 'opacity-50'}`
                }
                file={file}
                src={imageSrc}
                alt="No Thumbnail"
              />
            )}
            {!this.state.rotate && !isUploading && progress > 0 && !hasError && (
              <FontAwesomeIcon
                icon="check-circle"
                size="3x"
                className="position-absolute color-success"
              />
            )}
            {!isUploading && hasError && (
              <FontAwesomeIcon
                icon="exclamation-triangle"
                size="3x"
                className="position-absolute color-danger"
              />
            )}
          </div>
          {isUploading && <Progress className="mb-2" value={progress} />}
          <div id="file-actions" className={!isUploading ? 'mb-2' : ''}>
            <Button className="mt-0" color="link" onClick={cancelDeleteOnClick}>
              {(isUploading || progress === 0) && !hasError ? 'Cancel upload' : 'Delete'}
            </Button>
            {!isUploading && progress !== 0 && !hasError && (
              <>
                {!!this.state.rotate && (
                  <Button
                    className="mt-0 ml-2"
                    color="link"
                    onClick={this.saveImageHandler.bind(this)}
                  >
                    Save
                  </Button>
                )}
                <div id="action-arrows">
                  <RotateRightOutlined
                    fontSize="large"
                    onClick={() => this.handleRotateChange(true)}
                  />
                  <RotateLeftRounded
                    fontSize="large"
                    ml={2}
                    onClick={() => this.handleRotateChange(false)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {hasError && <div className="text-danger my-2">{file.error.description}</div>}
      </Col>
    );
  }
}

export default Step4File;
